import React, { useState, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert,
  Container,
} from "@mui/material";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { formatJpDateTime } from "../../../../utils/dateUtils";
import ConfirmDeleteDialog from "../../../../shared/ConfirmDeleteDialog";
import UserModal from "../../../users/UserModal";

export default function MemberListPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const location = useLocation();
  const [flashMessage, setFlashMessage] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [companyRoleKey, setCompanyRoleKey] = useState(null);
  useEffect(() => {
    const el = document.querySelector("[data-company-role]");
    setCompanyRoleKey(el?.dataset?.companyRole ?? null);
  }, []);

  // 一覧取得を共通関数化
  const fetchMembers = () => {
    setLoading(true);
    fetch("/company/members.json")
      .then((res) => res.json())
      .then((data) => {
        setMembers(data.users || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // 初回マウント時にメンバー一覧を取得
  useEffect(() => {
    fetchMembers();
  }, [location.key]); // 戻ってくるたびに再取得される

  useEffect(() => {
    // currentUser を data 属性から取得
    const root = document.querySelector(".react-app-router");
    if (root?.dataset?.currentUser) {
      try {
        setCurrentUser(JSON.parse(root.dataset.currentUser));
      } catch {
        setCurrentUser(null);
      }
    }
  }, []);

  useEffect(() => {
    // SPA遷移時はlocation.stateで拾う
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      // 履歴クリアも忘れずに
      window.history.replaceState({}, document.title, location.pathname);
      return;
    }

    // SSR/リロード時はdata属性から拾う
    const root = document.querySelector(".react-app-router");
    if (root && root.dataset.flashMessage) {
      try {
        setFlashMessage(JSON.parse(root.dataset.flashMessage));
        // data属性消しておくと安全（同じメッセージが何度も表示されるのを防ぐため）
        root.dataset.flashMessage = "";
      } catch (e) {
        setFlashMessage(root.dataset.flashMessage);
        root.dataset.flashMessage = "";
      }
    }
  }, [location.key, location.state, location.pathname]);

  // フラッシュメッセージがセットされたら上部にスクロール
  useEffect(() => {
    if (flashMessage) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [flashMessage]);

  // 削除ダイアログを開く
  const handleOpenDialog = (user) => {
    setTargetUser(user);
    setShowDialog(true);
  };

  // 削除ダイアログを閉じる（削除中は不可）
  const handleCloseDialog = () => {
    if (isDeleting) return;
    setShowDialog(false);
    setTargetUser(null);
  };

  /**
   * 「削除」を確定したときの処理
   * DELETE リクエストを送り、完了後にページをリロード
   */
  const handleConfirmDelete = () => {
    if (isDeleting) return; // 2重発火防止
    setIsDeleting(true);
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;

    const apiUrl = `/company/members/${targetUser?.id}.json`;

    fetch(apiUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
    }).then((res) => {
      if (res.ok) {
        setIsDeleting(false);
        setShowDialog(false);
        toast.success("メンバーを削除しました");
        fetchMembers();
      } else {
        toast.error("削除に失敗しました");
      }
    });
  };

  const handleSubmitNew = async (fd) => {
    const csrfToken = document.querySelector(
      'meta[name="csrf-token"]'
    )?.content;

    // role を employee に
    if (fd instanceof FormData) {
      fd.set("company_user[role]", "employee");
    }

    return fetch("/company/users.json", {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken },
      body: fd,
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 422) {
            const data = await res.json().catch(() => ({}));
            if (Array.isArray(data?.errors)) {
              return Promise.reject(data.errors);
            }
            if (data?.errors && typeof data.errors === "object") {
              const messages = Object.entries(data.errors).flatMap(
                ([field, msgs]) =>
                  Array.isArray(msgs)
                    ? msgs.map((m) => `${field} ${m}`)
                    : [`${field} ${msgs}`]
              );
              return Promise.reject(messages);
            }
            return Promise.reject(["バリデーションエラーが発生しました。"]);
          }
          const text = await res.text().catch(() => "");
          throw new Error(`Failed to create user: ${res.status}\n${text}`);
        }
        return res.json();
      })
      .then((data) => {
        setShowUserModal(false);
        toast.success("メンバーを追加しました");
        fetchMembers();
      })
      .catch((err) => {
        if (Array.isArray(err)) throw err;
        console.error(err);
        toast.error("追加に失敗しました");
        throw err;
      });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 10 }}>
        <Typography>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <>
      {/* フラッシュメッセージ*/}
      {flashMessage && (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert
            severity={
              typeof flashMessage === "object" ? flashMessage.type : "success"
            }
          >
            {typeof flashMessage === "object"
              ? flashMessage.message
              : flashMessage}
          </Alert>
        </Box>
      )}

      <Container sx={{ mt: 4, mb: 10 }}>
        <div className="flex items-center justify-between mb-6">
          <Typography variant="h5" fontWeight="bold">
            メンバー管理
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setShowUserModal(true)}
            sx={{ fontWeight: "bold" }}
          >
            ＋ 追加
          </Button>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={200}
            >
              <CircularProgress />
            </Box>
          ) : members.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ mt: 4, textAlign: "center" }}
            >
              メンバーが登録されていません
            </Typography>
          ) : (
            // 一覧テーブル
            <table className="w-full table-auto border border-gray-200 bg-white shadow-sm rounded-lg">
              <thead>
                <tr className="bg-blue-50 text-gray-800 text-xs">
                  {[
                    "No",
                    "氏名",
                    "イニシャル",
                    "年齢",
                    "性別",
                    "最寄り駅",
                    "最終学歴",
                    "エンジニア歴",
                    "最終更新日",
                    "詳細",
                    "削除",
                  ].map((header) => (
                    <th
                      key={header}
                      className="py-2 px-4 border-b whitespace-nowrap w-1/11"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((user, idx) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gray-50 text-center text-xs"
                  >
                    <td className="py-2 px-4 border-b">{idx + 1}</td>
                    <td className="py-2 px-4 border-b">{user.full_name}</td>
                    <td className="py-2 px-4 border-b">{user.name || "-"}</td>
                    <td className="py-2 px-4 border-b">
                      {user.age ? `${user.age}歳` : "-"}
                    </td>
                    <td className="py-2 px-4 border-b">{user.gender || "-"}</td>
                    <td className="py-2 px-4 border-b whitespace-nowrap max-w-[200px] overflow-hidden text-ellipsis">
                      <span className="block truncate">
                        {user.nearest_station_line && user.nearest_station
                          ? `${user.nearest_station_line} ${user.nearest_station}`
                          : user.nearest_station_line
                          ? user.nearest_station_line
                          : user.nearest_station
                          ? user.nearest_station
                          : "-"}
                      </span>
                    </td>
                    <td className="py-2 px-4 border-b whitespace-nowrap max-w-[200px] overflow-hidden text-ellipsis">
                      <span className="block truncate">
                        {user.final_academic_background || "-"}
                      </span>
                    </td>
                    <td className="py-2 px-4 border-b">
                      {user.work_histories_flag
                        ? user.engineer_history
                        : "未作成"}
                    </td>
                    <td className="py-2 px-4 border-b">
                      {user?.updated_at
                        ? formatJpDateTime(user.updated_at)
                        : "-"}
                    </td>
                    <td className="py-2 px-4 border-b whitespace-nowrap text-center">
                      {user.skill_sheet_flag ? (
                        <a
                          href={`/company/members/${user.id}/skill_sheet`}
                          className="text-indigo-600 underline hover:text-indigo-900"
                        >
                          詳細
                        </a>
                      ) : (
                        "未作成"
                      )}
                    </td>
                    <td className="py-2 px-4 border-b">
                      {/* 削除ボタン */}
                      <div className="flex justify-center">
                        <Button
                          color="error"
                          variant="outlined"
                          size="small"
                          onClick={() => handleOpenDialog(user)}
                          disabled={isDeleting}
                        >
                          削除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Container>
      {/* 削除確認ダイアログ */}
      <ConfirmDeleteDialog
        open={showDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
      />
      {/* 追加モーダル */}
      <UserModal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
        mode="new"
        initialValues={{ role: "employee" }}
        onSubmit={handleSubmitNew}
        currentUser={currentUser}
        allUsers={members}
        companyRoleKey={companyRoleKey}
      />
    </>
  );
}
